// Time helpers — gives the UI honest, locale-aware context about
// "now in Amsterdam" without lying about whether a venue is currently open.

const AMS_TZ = "Europe/Amsterdam";

/** Amsterdam-local calendar year for a Date, via Intl (DST-safe). */
export function amsterdamYear(d: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat("en-GB", { timeZone: AMS_TZ, year: "numeric" }).format(d));
}

/**
 * Long event date for the detail page. Includes time. Omits the year
 * when it matches the current Amsterdam year so in-year events read
 * compact ("Fri 26 Mar, 22:00 – 28 Mar"). 2027 events keep the year
 * ("Fri 26 Mar 2027, 22:00 – 28 Mar 2027") so they don't look like
 * stale 2026 events.
 */
export function formatEventDateRange(startsAt: number, endsAt: number | null = null): string {
  const start = new Date(startsAt * 1000);
  const end = endsAt && endsAt !== startsAt ? new Date(endsAt * 1000) : null;
  const nowYear = amsterdamYear(new Date());
  const startYear = amsterdamYear(start);
  const endYear = end ? amsterdamYear(end) : startYear;
  const startFmt = new Intl.DateTimeFormat("en-GB", {
    weekday: "short", day: "numeric", month: "short",
    ...(startYear !== nowYear ? { year: "numeric" as const } : {}),
    hour: "2-digit", minute: "2-digit", hour12: false,
    timeZone: AMS_TZ,
  }).format(start);
  if (!end) return startFmt;
  const endFmt = new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "short",
    ...(endYear !== nowYear ? { year: "numeric" as const } : {}),
    timeZone: AMS_TZ,
  }).format(end);
  return `${startFmt} – ${endFmt}`;
}

/**
 * Compact date for list rows — no time, no weekday. "26 Mar" /
 * "26–28 Mar" / "26 Mar 2027" depending on range + year.
 */
export function formatEventDateShort(startsAt: number, endsAt: number | null = null): string {
  const start = new Date(startsAt * 1000);
  const end = endsAt && endsAt !== startsAt ? new Date(endsAt * 1000) : null;
  const nowYear = amsterdamYear(new Date());
  const startYear = amsterdamYear(start);
  const endYear = end ? amsterdamYear(end) : startYear;
  const yearOpt = startYear !== nowYear ? { year: "numeric" as const } : {};
  const startFmt = new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "short",
    ...yearOpt,
    timeZone: AMS_TZ,
  }).format(start);
  if (!end) return startFmt;
  // Same month + same year → "26–28 Mar". Otherwise fall back to the
  // full range so the boundary month / year is unambiguous.
  const startDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", timeZone: AMS_TZ }).format(start);
  const startMonth = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: AMS_TZ }).format(start);
  const endDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", timeZone: AMS_TZ }).format(end);
  const endMonth = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: AMS_TZ }).format(end);
  if (startMonth === endMonth && startYear === endYear) {
    return `${startDay}–${endDay} ${startMonth}${startYear !== nowYear ? ` ${startYear}` : ""}`;
  }
  const endFmt = new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "short",
    ...(endYear !== nowYear ? { year: "numeric" as const } : {}),
    timeZone: AMS_TZ,
  }).format(end);
  return `${startFmt} – ${endFmt}`;
}

/**
 * Current hour (0..23) in Europe/Amsterdam. Uses Intl so DST is handled.
 */
export function amsterdamHour(date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Amsterdam",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const h = parts.find((p) => p.type === "hour")?.value;
  return Number(h ?? "0");
}

export type TimeBucket = "morning" | "afternoon" | "evening" | "late-night" | "post-midnight";

export function bucketForHour(h: number): TimeBucket {
  if (h < 4) return "post-midnight";    // 00:00–03:59 — most things closed
  if (h < 6) return "late-night";       // 04:00–05:59 — winding down
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  if (h < 24) return "evening";
  return "evening";
}

/**
 * Parse "Open · until 17:00" / "Locals only · until 03:00" / "21:00–02:00"
 * and return the closing hour if we can find one. Conservative — returns
 * null when we can't tell.
 */
export function parseCloseHour(text?: string | null): number | null {
  if (!text) return null;
  // Match HH:MM patterns; the LAST one is usually the close time.
  const matches = Array.from(text.matchAll(/\b(\d{1,2}):(\d{2})\b/g));
  if (!matches.length) return null;
  const last = matches[matches.length - 1];
  const h = Number(last[1]);
  return Number.isFinite(h) ? h : null;
}

/**
 * Is this venue "actually" open right now based on its hint string?
 *   - "Open until 17:00" + amsHour=3 → false (closed since 17:00 yesterday)
 *   - "Open until 02:00" + amsHour=23 → true (still open tonight)
 *   - "until 03:00" + amsHour=2 → true (open through the night)
 */
export function isOpenNow(nowText: string | null | undefined, amsHour = amsterdamHour()): boolean | null {
  const closeH = parseCloseHour(nowText);
  if (closeH == null) return null;

  // Late-night close (00:00–05:59) likely means "open through this night".
  if (closeH < 6) {
    return amsHour >= 18 || amsHour < closeH;
  }
  // Otherwise treat as a regular daytime close. Assume "open from morning".
  return amsHour >= 8 && amsHour < closeH;
}

/**
 * A friendly note to show when the user is browsing in the small hours.
 * Returns null when not applicable (so callers can just `&&` render it).
 */
export function lateNightNudge(amsHour = amsterdamHour()): { headline: string; sub: string } | null {
  if (amsHour >= 6 && amsHour < 4 + 24) {
    // 06:00 onwards — not "late". (Adjusted so 04:00–05:59 still counts as "late".)
  }
  if (amsHour >= 4 && amsHour < 6) {
    return {
      headline: "It's still early-late.",
      sub: "Most kitchens closed an hour ago. Showing tonight + tomorrow.",
    };
  }
  if (amsHour < 4) {
    return {
      headline: "Whoa — it's the middle of the night.",
      sub: "Listed hours are for the day they happen on. Showing tonight + tomorrow.",
    };
  }
  return null;
}
